// src/app/modules/Auth/components/AddressAutocomplete.jsx
import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search, Loader } from 'lucide-react';
import geocodingService from '../../../../services/geocodingService';
import './AddressAutocomplete.scss';

const AddressAutocomplete = ({
  formik,
  onAddressSelect,
  placeholder = "Start typing your address...",
  countryCode,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceTimer = useRef(null);
  const wrapperRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search for addresses as user types
  const handleSearch = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const results = await geocodingService.searchAddress(query, countryCode);
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Address search error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  // Handle address selection
  const handleSelectAddress = (suggestion) => {
    const { address, coordinates } = suggestion;

    // Update formik fields
    formik.setFieldValue('street', address.street);
    formik.setFieldValue('city', address.city);
    formik.setFieldValue('state', address.state);

    // Update search query
    setSearchQuery(suggestion.display_name);

    // Hide suggestions
    setShowSuggestions(false);

    // Callback with full details including coordinates
    if (onAddressSelect) {
      onAddressSelect({
        address,
        coordinates,
        display_name: suggestion.display_name,
      });
    }
  };

  // Manual geocoding when user fills fields manually
  const handleManualGeocode = async () => {
    if (!formik.values.street || !formik.values.city) {
      // Clear coordinates if address is incomplete
      if (onAddressSelect) {
        onAddressSelect(null);
      }
      return;
    }

    setLoading(true);

    try {
      const coordinates = await geocodingService.geocodeAddress({
        street: formik.values.street,
        city:   formik.values.city,
        state:  formik.values.state,
      });

      if (coordinates && onAddressSelect) {
        onAddressSelect({
          address: {
            street: formik.values.street,
            city:   formik.values.city,
            state:  formik.values.state,
          },
          coordinates: {
            lat: coordinates.lat,
            lon: coordinates.lon,
          },
          display_name: coordinates.display_name,
        });
      } else {
        // Failed to geocode - clear coordinates
        if (onAddressSelect) {
          onAddressSelect(null);
        }
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      // Clear coordinates on error
      if (onAddressSelect) {
        onAddressSelect(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="address-autocomplete" ref={wrapperRef}>
      <div className="search-input-wrapper">
        <div className="input-with-icon">
          <Search size={18} className="input-icon" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder={placeholder}
            className="form-control with-icon"
            autoComplete="off"
          />
          {loading && (
            <Loader size={18} className="input-icon-right spinning" />
          )}
        </div>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="suggestions-dropdown">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              className="suggestion-item"
              onClick={() => handleSelectAddress(suggestion)}
            >
              <MapPin size={16} className="suggestion-icon" />
              <div className="suggestion-content">
                <div className="suggestion-main">{suggestion.display_name}</div>
                {suggestion.type && (
                  <div className="suggestion-type">{suggestion.type}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Manual address fields */}
      <div className="manual-address-fields">
        <div className="form-group">
          <label className="form-label">Street Address</label>
          <input
            type="text"
            name="street"
            placeholder="123 Main Street"
            autoComplete="street-address"
            className={`form-control ${
              formik.touched.street && formik.errors.street ? 'error' : ''
            }`}
            {...formik.getFieldProps('street')}
            onBlur={(e) => {
              formik.handleBlur(e);
              handleManualGeocode();
            }}
          />
          {formik.touched.street && formik.errors.street && (
            <div className="form-error">{formik.errors.street}</div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">City</label>
            <input
              type="text"
              name="city"
              placeholder="City"
              autoComplete="address-level2"
              className={`form-control ${
                formik.touched.city && formik.errors.city ? 'error' : ''
              }`}
              {...formik.getFieldProps('city')}
              onBlur={(e) => {
                formik.handleBlur(e);
                handleManualGeocode();
              }}
            />
            {formik.touched.city && formik.errors.city && (
              <div className="form-error">{formik.errors.city}</div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">State</label>
            <input
              type="text"
              name="state"
              placeholder="State / Province"
              autoComplete="address-level1"
              className={`form-control ${
                formik.touched.state && formik.errors.state ? 'error' : ''
              }`}
              {...formik.getFieldProps('state')}
              onBlur={(e) => {
                formik.handleBlur(e);
                handleManualGeocode();
              }}
            />
            {formik.touched.state && formik.errors.state && (
              <div className="form-error">{formik.errors.state}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressAutocomplete;